import { DialogComponent } from './dialog.js';

/**
 * A custom search modal component that uses .show() instead of .showModal()
 * to avoid creating a backdrop element.
 *
 * @extends DialogComponent
 */
export class SearchModalComponent extends DialogComponent {
  /**
   * Shows the dialog using .show() instead of .showModal() to avoid backdrop
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Don't lock scroll for search modal - we want the page to remain interactive
    dialog.show();
    this.dispatchEvent(new CustomEvent('dialog:open'));

    // Wait until the next tick to add the event listeners to avoid race condition
    setTimeout(() => {
      this.addEventListener('keydown', this.#handleKeyDown);
      document.addEventListener('click', this.#handleClickOutside, true);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleClickOutside, true);

    dialog.classList.add('dialog-closing');

    // Wait for animation to complete
    await new Promise(resolve => setTimeout(resolve, 200));

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  /**
   * Closes the modal when clicking outside of it
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClickOutside = (event) => {
    const { dialog } = this.refs;
    
    // Check if the click is outside the dialog
    if (!dialog.contains(event.target)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }
}

if (!customElements.get('search-modal-component')) {
  customElements.define('search-modal-component', SearchModalComponent);
}

